import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Reveal from '@/components/feature/Reveal';
import { useTranslation } from 'react-i18next';

export default function Contact() {
  const { t } = useTranslation();

  const phoneNumbers = ['+41 26 436 10 83', '+41 79 887 72 52'];
  const phoneHrefs = ['tel:+41264361083', 'tel:+41798877252'];

  return (
    <>
      <Navbar />
      <main className="min-h-screen pb-12 bg-background-50">
        {/* Unified hero + contact section with one background image */}
        <div className="relative w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Bright%20professional%20cleaning%20service%20hero%20photo%2C%20cleaner%20with%20blue%20gloves%20and%20yellow%20cloth%20wiping%20a%20sparkling%20white%20glass%20window%20in%20a%20modern%20office%2C%20vivid%20daylight%20from%20window%20behind%2C%20clean%20clear%20background%2C%20unmistakable%20cleaning%20action%20front%20and%20center%2C%20fresh%20bright%20colors%2C%20high%20contrast%20and%20sharp%20details%2C%20cheerful%20and%20clean%20atmosphere&width=1600&height=800&seq=contact-hero-03&orientation=landscape&nocache=true"
              alt="Professional cleaning service with sparkling surfaces and fresh atmosphere"
              title="Professional cleaning services Switzerland"
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>

          {/* Hero text */}
          <div className="relative z-10 w-full px-4 md:px-6 pt-10 md:pt-16 pb-8 flex items-center justify-center">
            <div className="max-w-3xl mx-auto text-center">
              <Reveal>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/80 text-primary-700 text-xs font-semibold mb-5 backdrop-blur-sm">
                  <i className="ri-customer-service-2-line"></i>
                  {t('contactPage.badge')}
                </span>
              </Reveal>
              <Reveal delay={80}>
                <h1 className="font-heading text-3xl md:text-5xl font-bold text-white mb-5 drop-shadow-lg shadow-black/50">
                  {t('contactPage.title')}
                </h1>
              </Reveal>
              <Reveal delay={160}>
                <p className="text-white/90 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
                  {t('contactPage.subtitle')}
                </p>
              </Reveal>
            </div>
          </div>

          {/* Contact cards + map floating over the same image */}
          <div className="relative z-10 w-full px-4 md:px-6 pb-20">
            <div className="max-w-5xl mx-auto rounded-2xl border border-white/40 overflow-hidden bg-white/55 backdrop-blur-lg shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-foreground-900/10">
                <Reveal delay={0}>
                  <div className="h-full p-8 flex flex-col items-center text-center transition-all duration-300 hover:bg-white/40">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-600 text-white mb-5 shadow-md">
                      <i className="ri-phone-line text-2xl"></i>
                    </div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground-700 mb-4">
                      {t('contactPage.phone')}
                    </h2>
                    <div className="flex flex-col gap-2">
                      {phoneNumbers.map((number, index) => (
                        <a
                          key={number}
                          href={phoneHrefs[index]}
                          className="text-foreground-950 font-semibold hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                        >
                          {number}
                        </a>
                      ))}
                    </div>
                  </div>
                </Reveal>

                <Reveal delay={100}>
                  <div className="h-full p-8 flex flex-col items-center text-center transition-all duration-300 hover:bg-white/40">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-600 text-white mb-5 shadow-md">
                      <i className="ri-mail-line text-2xl"></i>
                    </div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground-700 mb-4">
                      {t('contactPage.email')}
                    </h2>
                    <a
                      href="mailto:info@limpiasarl.ch"
                      className="text-foreground-950 font-semibold hover:text-primary-700 transition-colors break-all cursor-pointer"
                    >
                      info@limpiasarl.ch
                    </a>
                  </div>
                </Reveal>

                <Reveal delay={200}>
                  <div className="h-full p-8 flex flex-col items-center text-center transition-all duration-300 hover:bg-white/40">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-600 text-white mb-5 shadow-md">
                      <i className="ri-map-pin-line text-2xl"></i>
                    </div>
                    <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground-700 mb-4">
                      {t('contactPage.address')}
                    </h2>
                    <p className="text-foreground-950 text-sm font-semibold leading-relaxed">
                      {t('footer.address')}
                    </p>
                  </div>
                </Reveal>
              </div>

              <div className="border-t border-foreground-900/10">
                <div className="px-4 md:px-8 pt-6">
                  <Reveal>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6 text-center">
                      {t('contactPage.mapTitle')}
                    </h2>
                  </Reveal>
                </div>
                <div className="w-full aspect-[16/9] md:aspect-[21/9]">
                  <iframe
                    title={t('contactPage.mapTitle')}
                    src="https://www.google.com/maps?q=Rte%20de%20Ros%C3%A9%2051%2C%201754%20Avry-sur-Matran%2C%20Suisse&output=embed"
                    className="w-full h-full border-0"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}